import { SyntheticEvent, useId, useState } from "react";
import HumanMessageContent from "duck/ui/chat/components/messages/HumanMessageContent";
import SafeModeLabel from "duck/ui/chat/components/messages/SafeModeLabel";
import UploadedFile from "duck/ui/chat/components/UploadedFile";
import { getDirectiveEnvelopePlainText } from "duck/ui/chat/directiveEnvelope";
import {
  AttachmentFileInfo,
  MessageContent,
  UserContentStructure,
} from "duck/ui/chat/serverSentEvents";
import { ChatMessage } from "duck/ui/chat/types";
import { formatHumanMessage } from "duck/ui/chat/utils";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";

import Accordion from "features/components/Accordion";
import Typography from "features/components/Typography";

interface Props {
  message: ChatMessage;
  isSubthread?: boolean;
}

const isTextUserContentStructure = (
  value: unknown
): value is UserContentStructure => {
  if (!value || typeof value !== "object") return false;

  const record = value as Record<string, unknown>;

  return record.type === "text" && typeof record.text === "string";
};

const hasElementsArray = (
  value: MessageContent
): value is { elements: UserContentStructure[] } => {
  if (!value || typeof value !== "object") return false;

  const record = value as Record<string, unknown>;

  return Array.isArray(record.elements);
};

const getAttachmentFileInfo = (
  message: ChatMessage,
  uploadFailedMessage: string
): AttachmentFileInfo[] => {
  const content = message.content;
  if (!hasElementsArray(content)) return [];

  return content.elements
    .slice(1)
    .map((attachment): AttachmentFileInfo | null => {
      if (!isTextUserContentStructure(attachment)) return null;

      const attachmentText = attachment.text ?? "";

      // Test quickly to see if it is possible for it to be a valid JSON object.
      // This is faster than the more thorough validation that follows below.
      if (!attachmentText.trim().startsWith("{")) return null;

      try {
        const parsed: unknown = JSON.parse(attachmentText);
        if (!parsed || typeof parsed !== "object") return null;

        const parsedRecord = parsed as Record<string, unknown>;
        const fileName =
          typeof parsedRecord.file_name === "string"
            ? parsedRecord.file_name
            : null;
        if (!fileName) return null;

        const hasError = Object.prototype.hasOwnProperty.call(
          parsedRecord,
          "error"
        );
        const errorValue = parsedRecord.error;
        const errorMessage =
          hasError && typeof errorValue === "string" && errorValue.trim()
            ? errorValue
            : hasError
              ? uploadFailedMessage
              : undefined;

        return errorMessage ? { fileName, errorMessage } : { fileName };
      } catch {
        return null;
      }
    })
    .filter((fileInfo): fileInfo is AttachmentFileInfo => Boolean(fileInfo));
};

const HumanMessage = ({ message, isSubthread = false }: Props) => {
  const { t } = useTranslation("duck");
  const attachmentFiles = getAttachmentFileInfo(message, t("upload_failed"));
  const formattedContent = formatHumanMessage(message);
  const contentId = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = (_event: SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
  };

  // in subthread context, display as an extracted context message rather than a user-sent bubble
  if (isSubthread) {
    return (
      <Box className="w-full flex justify-start">
        <Box className="w-full text-left">
          {message.safeMode && <SafeModeLabel />}
          <Accordion
            id={contentId}
            title={
              <Typography variant="overline" className="!text-gray-400 flex">
                {t("task")}
              </Typography>
            }
            expanded={isExpanded}
            onChange={handleChange}
            size="small"
          >
            <Typography variant="body3">
              {getDirectiveEnvelopePlainText(formattedContent)}
            </Typography>
          </Accordion>
        </Box>
      </Box>
    );
  }

  return (
    <div className="flex justify-end ml-12 mt-4 mb-6">
      <div className="flex flex-col items-end">
        {message.safeMode && <SafeModeLabel />}
        <div className="p-2 px-4 bg-slate-100 rounded-tl-lg rounded-tr-lg rounded-bl-lg text-gray-700 max-w-full wrap-anywhere">
          <HumanMessageContent content={formattedContent} />
        </div>
        {attachmentFiles.length > 0 && (
          <div className="mt-1 flex flex-wrap justify-end gap-1">
            {attachmentFiles.map((file, index) => (
              <UploadedFile
                key={`${file.fileName}-${index}`}
                fileName={file.fileName}
                errorMessage={file.errorMessage}
                canDelete={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HumanMessage;
